import { getEnvVariable } from '@appcharge/shared-ui';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ELocalStorageKeys, EQueryKeys } from '../constants/enums';
import useAxios, { baseURLPromise } from '../hooks/useAxios';
import { localStorageUtil } from '../utils/localStorageUtil';
import { Report } from '../views/Reports/types/report.types';
import {
  SearchReportsRequest,
  SearchReportsResponse
} from '../views/Reports/types/searchReports.types';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

const BASE_URL =
  localStorageUtil.getAny(ELocalStorageKeys.ENVIRONMENT) ||
  getEnvVariable('REACT_APP_API_BASE_URL');

export type UseReportsOptions = {
  searchRequest?: SearchReportsRequest;
  search?: { projects?: string; enabled?: boolean };
};

export default function useReports(options?: UseReportsOptions) {
  const axios = useAxios();
  const reportsRoute = EApiRoutes.FINANCIAL_REPORTS;
  const searchRequest = options?.searchRequest;
  const searchProjectsHeader = options?.search?.projects;
  const searchEnabled = options?.search?.enabled ?? true;
  const searchQueryEnabled =
    searchRequest !== undefined &&
    searchEnabled &&
    searchProjectsHeader !== undefined;

  const searchReports = useQuery<SearchReportsResponse>({
    ...fetchConfig.list,
    retry: false,
    queryKey: [
      EQueryKeys.FINANCIAL_REPORTS,
      searchRequest,
      searchProjectsHeader
    ],
    queryFn: async () => {
      await baseURLPromise();
      const response = await axios.post(
        `${getApiRouteOrMock(reportsRoute)}/search`,
        buildSearchReportsBody(searchRequest!),
        { 'x-publishers': searchProjectsHeader! }
      );
      return response.data;
    },
    enabled: searchQueryEnabled
  });

  const getReportDownload = (reportId: string) => {
    baseURLPromise().then(() => {
      window.open(
        `${BASE_URL}${getApiRouteOrMock(reportsRoute)}/${reportId}/download`,
        '_blank'
      );
    });
  };

  const createReport = useMutation(
    async (params: { data: Pick<Report, 'interval'>; projects?: string }) => {
      const reportsParams = params.data.interval;
      await baseURLPromise();
      return await axios.post(
        getApiRouteOrMock(reportsRoute),
        reportsParams,
        params?.projects ? { 'x-publishers': params?.projects } : undefined
      );
    },
    {}
  );

  const deleteReport = useMutation(async (reportId: string) => {
    await baseURLPromise();
    return await axios.del(`${getApiRouteOrMock(reportsRoute)}/${reportId}`);
  }, {});

  return { createReport, deleteReport, getReportDownload, searchReports };
}

function buildSearchReportsBody(
  searchRequest: SearchReportsRequest
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (searchRequest.sortDirection !== undefined) {
    body.sortDirection = searchRequest.sortDirection;
  }
  if (
    searchRequest.statuses !== undefined &&
    searchRequest.statuses.length > 0
  ) {
    body.statuses = searchRequest.statuses;
  }
  if (searchRequest.createdAfter !== undefined) {
    body.createdAfter = searchRequest.createdAfter;
  }
  if (searchRequest.createdBefore !== undefined) {
    body.createdBefore = searchRequest.createdBefore;
  }
  if (searchRequest.limit !== undefined) {
    body.limit = searchRequest.limit;
  }
  if (searchRequest.page !== undefined) {
    body.page = searchRequest.page;
  }
  return body;
}
